import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquarePlus } from 'lucide-react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface FeedbackButtonProps {
  isCollapsed?: boolean;
  className?: string;
}

type Category = 'bug' | 'feature' | 'general';

const CATEGORY_LABELS: Record<Category, string> = {
  bug: 'Bug Report',
  feature: 'Feature Request',
  general: 'General Feedback',
};

export function FeedbackButton({ isCollapsed = false, className }: FeedbackButtonProps) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category>('general');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const { pathname } = useLocation();
  const submitFeedback = useMutation(api.shared.feedback.submit);

  function resetForm() {
    setCategory('general');
    setMessage('');
    setSubmitError(null);
    setSubmitted(false);
  }

  function handleOpenChange(val: boolean) {
    setOpen(val);
    if (!val) resetForm();
  }

  async function handleSubmit() {
    if (!message.trim()) {
      setSubmitError('Please enter a message before submitting.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await submitFeedback({ category, message, pageUrl: pathname });
      setSubmitted(true);
      setTimeout(() => {
        setOpen(false);
        resetForm();
      }, 1500);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const triggerButton = (
    <button
      onClick={() => setOpen(true)}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all text-sm font-medium',
        isCollapsed && 'justify-center',
        className
      )}
    >
      <MessageSquarePlus className="w-5 h-5 flex-shrink-0" />
      {!isCollapsed && <span>Send Feedback</span>}
    </button>
  );

  return (
    <>
      {isCollapsed ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
          <TooltipContent side="right">Send Feedback</TooltipContent>
        </Tooltip>
      ) : (
        triggerButton
      )}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Feedback</DialogTitle>
            <DialogDescription>
              Found a bug, have a suggestion, or just want to share a thought? We'd love to hear it.
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="py-6 text-center">
              <p className="text-base font-medium text-foreground">Thanks for your feedback!</p>
              <p className="text-sm text-muted-foreground mt-1">We'll review it shortly.</p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="feedback-category">Category</Label>
                <Select value={category} onValueChange={(v) => setCategory(v as Category)}>
                  <SelectTrigger id="feedback-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.entries(CATEGORY_LABELS) as [Category, string][]).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="feedback-message">Message</Label>
                <Textarea
                  id="feedback-message"
                  placeholder="Describe your feedback..."
                  rows={4}
                  maxLength={2000}
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    if (submitError) setSubmitError(null);
                  }}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  {submitError ? (
                    <span className="text-destructive">{submitError}</span>
                  ) : (
                    <span />
                  )}
                  <span>{message.length}/2000</span>
                </div>
              </div>
            </div>
          )}

          {!submitted && (
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !message.trim()}>
                {isSubmitting ? 'Sending...' : 'Send Feedback'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
